import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldIcon, UsersIcon, KeyIcon, BuildingIcon, NetworkIcon, FileCheckIcon, MapIcon, SettingsIcon, ActivityIcon, CheckIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function ICAMPage() {
  return <div className="w-full" data-id="element-1598" data-mp="1jy05z239xoxp">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern" data-id="element-1599" data-mp="1o0od95ccansk">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1600" data-mp="1o27qso6m2kqn">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-1601" data-mp="1a26rve9wpndt">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto text-white" data-id="element-1602" data-mp="1ez6o6xgyfbrq">
              Federal ICAM & Zero Trust Solutions
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1603" data-mp="39dzkyztkq8l9"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-1604" data-mp="39bn1gi863q63">
              Identity, Credentialing, and Access Management for Department of
              Defense, Federal Agencies, and Enterprises
            </p>
            <p className="text-lg text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-1605" data-mp="33zymymbey6m8">
              New River Systems delivers comprehensive identity and access
              management solutions aligned with FICAM architecture, Zero Trust
              principles, and NIST security standards. Our security-cleared
              identity engineers implement and operate enterprise identity
              platforms for federal agencies and enterprises with the most
              stringent security requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1606" data-mp="2g8kxco4ncp4y">
              <Link to="/contact-us" className="btn-primary" data-id="element-1607" data-mp="hyp3zzknhmc">
                Request an ICAM Assessment
              </Link>
              <Link to="/services/identity-access-management/capabilities" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1608" data-mp="3rp1fwqbhzlw1">
                View ICAM Capabilities
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="section-white" data-id="element-1609" data-mp="2yaqnxbid4307">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1610" data-mp="33bwdj0gq7a5i">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1611" data-mp="26lngxnz3cejv">
            ICAM Expertise
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1612" data-mp="2ampg64t0cqlu"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1613" data-mp="2fm24ktzj25qj">
            <div className="card flex flex-col" data-id="element-1614" data-mp="2fjz5lkged29o">
              <ShieldIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1615" data-mp="2kd1z5sl6cbpv" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1616" data-mp="1mxehqtz4r8av">
                Zero Trust Architecture
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1617" data-mp="1rvzegjf6b9uk">
                Design and implement Zero Trust environments with strict
                identity verification, micro-segmentation, and continuous risk
                assessment. Never trust, always verify.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1618" data-mp="1aeslifzr62j8">
                Learn More →
              </Link>
            </div>

            <div className="card flex flex-col" data-id="element-1619" data-mp="11duerpxxjgwy">
              <UsersIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1620" data-mp="11foxqypn4yye" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1621" data-mp="wn4ayh55jm4d">
                Identity & Access Governance
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1622" data-mp="rp2qpsvzpgrb">
                Develop 'as-is' and 'to-be' identity architectures, establish
                policy frameworks, and implement role-based access control
                (RBAC) across enterprise systems.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1623" data-mp="do9vf5xv6hn5">
                Learn More →
              </Link>
            </div>

            <div className="card flex flex-col" data-id="element-1624" data-mp="2hrbe9lpren1u">
              <KeyIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1625" data-mp="2hu7an5lyl3o6" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1626" data-mp="240gbmr5ut26p">
                Credential Management & SSO
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1627" data-mp="28xycdu9pwb3m">
                Implement phishing-resistant credentials, PIV/CAC card
                enablement, Single Sign-On (SSO), and multi-factor
                authentication (MFA) for secure, seamless access.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1628" data-mp="1l3omw27jluv9">
                Learn More →
              </Link>
            </div>

            <div className="card flex flex-col" data-id="element-1629" data-mp="3fgwjnzf2wgjj">
              <BuildingIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1630" data-mp="3kfrdrxihxriy" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1631" data-mp="3kcveo5uwwwsx">
                PACS + LACS Integration
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1632" data-mp="3pkbmwjvw933i">
                Unify Physical Access Control Systems (PACS) with Logical Access
                Control Systems (LACS) for consistent badge and credential
                management across facilities and networks.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1633" data-mp="h49r66hywa83">
                Learn More →
              </Link>
            </div>

            <div className="card flex flex-col" data-id="element-1634" data-mp="q4ogdgy1y9gv">
              <NetworkIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1635" data-mp="q4ep7q19tw9l" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1636" data-mp="m338yb4aene2">
                Platform Integration & Engineering
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1637" data-mp="2rwtg3elsmb25">
                Deploy and integrate leading identity platforms including
                SailPoint, CyberArk, OKTA, Azure AD, AWS IAM, and Active
                Directory with custom workflows and connectors.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1638" data-mp="vu7z6um07gw4">
                Learn More →
              </Link>
            </div>

            <div className="card flex flex-col" data-id="element-1639" data-mp="15s44v6w69jmu">
              <FileCheckIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1640" data-mp="19sxt61dj10g9" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1641" data-mp="19qv6nrbl1pga">
                Compliance & Assessment
              </h3>
              <p className="text-gray-600 mb-4 flex-grow" data-id="element-1642" data-mp="1epo98opbzw1o">
                Ensure compliance with NIST SP 800-53, 800-171, OMB M-19-17,
                M-22-09, FedRAMP, FISMA, RMF, and FICAM architecture
                requirements through continuous assessment.
              </p>
              <Link to="/services/identity-access-management/capabilities" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-1643" data-mp="1teu5c3svh65c">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM EXPERTISE */}
      <section className="section-light-blue" data-id="element-1644" data-mp="3dxegfpiz3dfd">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1645" data-mp="38zmnqtai7na4">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1646" data-mp="391z637k6bznp">
            Identity Platform Expertise
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-1647" data-mp="5j63tqwuix8u"></div>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto" data-id="element-1648" data-mp="5l8wkipdv4h9">
            Our certified identity engineers have deep expertise implementing
            and operating leading identity platforms:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1649" data-mp="3n05u7vzbmla6">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200" data-id="element-1650" data-mp="3mxjkpqsvdvpb">
              <h3 className="text-2xl font-bold mb-6 text-nrs-navy border-b border-gray-200 pb-4" data-id="element-1651" data-mp="oaypt1dhzi72">
                Identity Governance & Administration (IGA)
              </h3>

              <div className="mb-6" data-id="element-1652" data-mp="3nzyoo1d13puq">
                <h4 className="font-bold text-lg mb-2" data-id="element-1653" data-mp="3nyxa3mwt0l89">
                  SailPoint IdentityIQ / IdentityNow
                </h4>
                <ul className="space-y-1 text-gray-600 ml-4 list-disc" data-id="element-1654" data-mp="3f0lltrjetziz">
                  <li data-id="element-1655" data-mp="3j1nchjz9h39c">Identity lifecycle management</li>
                  <li data-id="element-1656" data-mp="3j2gkeo9yhyt2">Access certification and governance</li>
                  <li data-id="element-1657" data-mp="alc3mjle1xpn">Role mining and RBAC design</li>
                  <li data-id="element-1658" data-mp="amn4aajjepcj">Compliance reporting</li>
                </ul>
              </div>

              <div data-id="element-1659" data-mp="fkeyxcbkgpkf">
                <h4 className="font-bold text-lg mb-2" data-id="element-1660" data-mp="fiuck9zeocxn">
                  Microsoft Azure AD / Entra ID
                </h4>
                <ul className="space-y-1 text-gray-600 ml-4 list-disc" data-id="element-1661" data-mp="6k09c0rkkob4">
                  <li data-id="element-1662" data-mp="6md115n4qd1d">Cloud identity and access management</li>
                  <li data-id="element-1663" data-mp="2malfrpd6agej">Conditional access policies</li>
                  <li data-id="element-1664" data-mp="2rahlpu5fkhwq">Privileged Identity Management (PIM)</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200" data-id="element-1665" data-mp="2isxobvw25m6j">
              <h3 className="text-2xl font-bold mb-6 text-nrs-navy border-b border-gray-200 pb-4" data-id="element-1666" data-mp="2es5ep1j9xf9q">
                Privileged Access & Authentication
              </h3>

              <div className="mb-6" data-id="element-1667" data-mp="2spk80hcne76z">
                <h4 className="font-bold text-lg mb-2" data-id="element-1668" data-mp="2ns20mvea4013">
                  CyberArk Privileged Access Security
                </h4>
                <ul className="space-y-1 text-gray-600 ml-4 list-disc" data-id="element-1669" data-mp="203r587rcqsk7">
                  <li data-id="element-1670" data-mp="1v4nx2eg2s680">Privileged account management</li>
                  <li data-id="element-1671" data-mp="1v1tg4ww9t3jc">Session monitoring and recording</li>
                  <li data-id="element-1672" data-mp="29tu3onh8sypx">Secrets management</li>
                </ul>
              </div>

              <div className="mb-6" data-id="element-1673" data-mp="251sksipkk9qx">
                <h4 className="font-bold text-lg mb-2" data-id="element-1674" data-mp="250r9cd4n5l8o">OKTA Identity Cloud</h4>
                <ul className="space-y-1 text-gray-600 ml-4 list-disc" data-id="element-1675" data-mp="1hb6pzt4kslyv">
                  <li data-id="element-1676" data-mp="1cdwtjtirra1j">Single Sign-On (SSO) and MFA</li>
                  <li data-id="element-1677" data-mp="1cbamrw3pcykd">Adaptive authentication</li>
                  <li data-id="element-1678" data-mp="3t18h6j7kg2zu">API access management</li>
                </ul>
              </div>

              <div data-id="element-1679" data-mp="3o1cc0gn4u5ip">
                <h4 className="font-bold text-lg mb-2" data-id="element-1680" data-mp="3o2x7q9l6nui5">
                  Active Directory / LDAP
                </h4>
                <ul className="space-y-1 text-gray-600 ml-4 list-disc" data-id="element-1681" data-mp="3j17tkl2nz0pu">
                  <li data-id="element-1682" data-mp="3e4hg85i8uj84">Directory services and federation</li>
                  <li data-id="element-1683" data-mp="3a248jbjxsb8p">Group Policy management</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE OFFERINGS */}
      <section className="section-white" data-id="element-1684" data-mp="359b3emkk30w3">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1685" data-mp="30brr3s7f4y6s">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1686" data-mp="2vd83lnd6fecb">
            ICAM Service Offerings
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1687" data-mp="2qdvgd1ie8597"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1688" data-mp="2mciairfaw22a">
            {/* Tier 1 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-navy" data-id="element-1689" data-mp="2mew4sy25pa8s">
              <MapIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1690" data-mp="11u7tb4dd8m38" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1691" data-mp="15v1hlyuq02wn">
                Tier 1: ICAM Strategy & Roadmap
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-1692" data-mp="1avp4c8bx1xl2">
                <li className="flex items-start gap-2" data-id="element-1693" data-mp="1fuih1yjdpkk6">
                  <span className="text-nrs-orange mt-1" data-id="element-1694" data-mp="1frwcm7rsbl66">•</span> Current state
                  assessment ("as-is" architecture)
                </li>
                <li className="flex items-start gap-2" data-id="element-1695" data-mp="n6mn31l726r3">
                  <span className="text-nrs-orange mt-1" data-id="element-1696" data-mp="s4o99vdx2m6s">•</span> Gap analysis
                  against FICAM and Zero Trust
                </li>
                <li className="flex items-start gap-2" data-id="element-1697" data-mp="ww6amkjuqosa">
                  <span className="text-nrs-orange mt-1" data-id="element-1698" data-mp="3vloacc9ehwl1">•</span> Future state
                  design ("to-be" architecture)
                </li>
                <li className="flex items-start gap-2" data-id="element-1699" data-mp="4dsj12p3zz1n">
                  <span className="text-nrs-orange mt-1" data-id="element-1700" data-mp="3sjjv2ffa4cjx">•</span> 12-24 month
                  implementation roadmap
                </li>
                <li className="flex items-start gap-2" data-id="element-1701" data-mp="3ohf3dm8e5pzx">
                  <span className="text-nrs-orange mt-1" data-id="element-1702" data-mp="63xq6bww8wy2">•</span> Business case
                  and ROI analysis
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-1703" data-mp="15ns9tjsbi8r">
                <p data-id="element-1704" data-mp="39k5wfk6ludn3">
                  <span className="font-bold text-nrs-navy" data-id="element-1705" data-mp="39lpcpas9goov">
                    Typical Duration:
                  </span>{' '}
                  60-90 days
                </p>
                <p className="mt-1" data-id="element-1706" data-mp="3jjdicvrenglt">
                  <span className="font-bold text-nrs-navy" data-id="element-1707" data-mp="3jkewxa7mql8a">Deliverables:</span>{' '}
                  Architecture diagrams, roadmap, business case
                </p>
              </div>
            </div>

            {/* Tier 2 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-orange" data-id="element-1708" data-mp="tts0omtqq9f5">
              <SettingsIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1709" data-mp="tvw6597afghj" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1710" data-mp="2bpv5adogfg15">
                Tier 2: Platform Implementation
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-1711" data-mp="2goessiip4zvm">
                <li className="flex items-start gap-2" data-id="element-1712" data-mp="22s1jhwobg4s8">
                  <span className="text-nrs-orange mt-1" data-id="element-1713" data-mp="27pjk8zs6jdp5">•</span> Platform
                  deployment and configuration
                </li>
                <li className="flex items-start gap-2" data-id="element-1714" data-mp="2vgozbcqaq1ye">
                  <span className="text-nrs-orange mt-1" data-id="element-1715" data-mp="2zgr2xrfld5gb">•</span> Identity data
                  migration and synchronization
                </li>
                <li className="flex items-start gap-2" data-id="element-1716" data-mp="2lp2srtowkr1z">
                  <span className="text-nrs-orange mt-1" data-id="element-1717" data-mp="2qo5mwj3chcv6">•</span> Custom
                  connector and workflow development
                </li>
                <li className="flex items-start gap-2" data-id="element-1718" data-mp="3ecoyb7hoy5rh">
                  <span className="text-nrs-orange mt-1" data-id="element-1719" data-mp="3j5i1hqxih61g">•</span> Integration
                  with existing systems (AD, HR, ticketing)
                </li>
                <li className="flex items-start gap-2" data-id="element-1720" data-mp="1uh2j74fjlzw0">
                  <span className="text-nrs-orange mt-1" data-id="element-1721" data-mp="1pj12gq5lacnp">•</span> User
                  acceptance testing and training
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-1722" data-mp="1gjdz3ytauws0">
                <p data-id="element-1723" data-mp="1gl8llhk7ywy7">
                  <span className="font-bold text-nrs-navy" data-id="element-1724" data-mp="1glqq11h1chh7">
                    Typical Duration:
                  </span>{' '}
                  6-12 months
                </p>
                <p className="mt-1" data-id="element-1725" data-mp="298v03kuzjlzu">
                  <span className="font-bold text-nrs-navy" data-id="element-1726" data-mp="24h37l4s9r672">Deliverables:</span>{' '}
                  Operational platform, documentation, trained administrators
                </p>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-navy" data-id="element-1727" data-mp="2wyfk872vkpap">
              <ActivityIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1728" data-mp="2s6nozj8rxve8" />
              <h3 className="text-xl font-bold mb-4" data-id="element-1729" data-mp="2zibvukiz1wie">
                Tier 3: Managed Services & Operations
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-1730" data-mp="34gdi1ebp2by3">
                <li className="flex items-start gap-2" data-id="element-1731" data-mp="39ghwkyt1kwlo">
                  <span className="text-nrs-orange mt-1" data-id="element-1732" data-mp="39eorvp1zomgb">•</span> 24/7 platform
                  monitoring and support
                </li>
                <li className="flex items-start gap-2" data-id="element-1733" data-mp="3ih7pnuqx48vp">
                  <span className="text-nrs-orange mt-1" data-id="element-1734" data-mp="3igeod9aqabky">•</span> User
                  provisioning and access requests
                </li>
                <li className="flex items-start gap-2" data-id="element-1735" data-mp="3s6zmywqejg3v">
                  <span className="text-nrs-orange mt-1" data-id="element-1736" data-mp="3s79befvstf7g">•</span> Access
                  certification campaigns
                </li>
                <li className="flex items-start gap-2" data-id="element-1737" data-mp="521o90pa00ep">
                  <span className="text-nrs-orange mt-1" data-id="element-1738" data-mp="53miep7ohhc1">•</span> Compliance
                  reporting and auditing
                </li>
                <li className="flex items-start gap-2" data-id="element-1739" data-mp="9ys5hrcth21p">
                  <span className="text-nrs-orange mt-1" data-id="element-1740" data-mp="2652jaeh6863r">•</span> Continuous
                  platform optimization
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-1741" data-mp="2ftsrw96pusbq">
                <p data-id="element-1742" data-mp="2fw5i19mmo8zv">
                  <span className="font-bold text-nrs-navy" data-id="element-1743" data-mp="2b3cfmsemtcqy">
                    Typical Duration:
                  </span>{' '}
                  Ongoing (annual contracts)
                </p>
                <p className="mt-1" data-id="element-1744" data-mp="2pu32rn5a3nnq">
                  <span className="font-bold text-nrs-navy" data-id="element-1745" data-mp="2ktym9x4desxi">SLA:</span> P1 (2hr
                  response), P2 (4hr response)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE & CERTIFICATIONS */}
      <section className="section-navy" data-id="element-1746" data-mp="14exfktvjl2yc">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1747" data-mp="14fytd63y03jr">
          <h2 className="text-3xl font-bold text-center mb-4 text-white" data-id="element-1748" data-mp="zgcc8lkzu35o">
            Compliance Expertise
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1749" data-mp="3b47hwjszlnc5"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1750" data-mp="3g2ipsozq1nrq">
            <div data-id="element-1751" data-mp="3g43o8pp5pquv">
              <h3 className="text-2xl font-bold mb-6 text-nrs-orange" data-id="element-1752" data-mp="32b494otadynu">
                Standards & Frameworks
              </h3>
              <ul className="space-y-3 text-blue-100" data-id="element-1753" data-mp="36bedxy5cej14">
                <li className="flex items-center gap-3" data-id="element-1754" data-mp="2scoewcyyq9a4">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1755" data-mp="2sb3l4pkh2udb" /> NIST SP
                  800-53 (Security Controls)
                </li>
                <li className="flex items-center gap-3" data-id="element-1756" data-mp="2jc01nkhe8cya">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1757" data-mp="2jdkw7a3pjvw5" /> NIST SP
                  800-171 (CUI Protection)
                </li>
                <li className="flex items-center gap-3" data-id="element-1758" data-mp="hkn6vo953lfy">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1759" data-mp="hl52cinz9vmt" /> OMB M-19-17
                  (Enabling Mission Delivery through ICAM)
                </li>
                <li className="flex items-center gap-3" data-id="element-1760" data-mp="35isr23apmwad">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1761" data-mp="35kc6xssgf5bm" /> OMB M-22-09
                  (Zero Trust Architecture)
                </li>
                <li className="flex items-center gap-3" data-id="element-1762" data-mp="2vry1aoy99ek6">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1763" data-mp="2vqd6qzbxxvmb" /> FICAM
                  (Federal ICAM Architecture)
                </li>
                <li className="flex items-center gap-3" data-id="element-1764" data-mp="2msb1u8p36itr">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1765" data-mp="2mposc3imxt8w" /> FedRAMP
                  (Cloud Authorization)
                </li>
                <li className="flex items-center gap-3" data-id="element-1766" data-mp="2ctc4vndoobml">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1767" data-mp="2ctu3gqntj1xo" /> FISMA / RMF
                  (Risk Management Framework)
                </li>
              </ul>
            </div>

            <div data-id="element-1768" data-mp="b1p6h0rgpeg3">
              <h3 className="text-2xl font-bold mb-6 text-nrs-orange" data-id="element-1769" data-mp="b04o09if9lyl">
                Team Certifications
              </h3>
              <ul className="space-y-3 text-blue-100" data-id="element-1770" data-mp="ek4ry0mizyc9">
                <li className="flex items-center gap-3" data-id="element-1771" data-mp="ejmy1anc5w7i">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1772" data-mp="il86mjsu7obw" /> CISSP
                  (Certified Information Systems Security Professional)
                </li>
                <li className="flex items-center gap-3" data-id="element-1773" data-mp="nejf0pdasd5x">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1774" data-mp="sai30buprt5t" /> CISA
                  (Certified Information Systems Auditor)
                </li>
                <li className="flex items-center gap-3" data-id="element-1775" data-mp="3rt120nlmwj9i">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1776" data-mp="3vt1lwnri5mny" /> CSEIP
                  (Certified System Engineer ICAM PACS)
                </li>
                <li className="flex items-center gap-3" data-id="element-1777" data-mp="9l1v33gp8yfd">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1778" data-mp="9mmryzqhktge" /> Active
                  Security Clearances
                </li>
                <li className="flex items-center gap-3" data-id="element-1779" data-mp="1l08gcioiur0v">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1780" data-mp="1kyxggty763f1" /> SailPoint
                  Certified Administrator
                </li>
                <li className="flex items-center gap-3" data-id="element-1781" data-mp="2wt7jcz2sovoi">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1782" data-mp="31stwz9mjcdxu" /> CyberArk
                  Certified Professional
                </li>
                <li className="flex items-center gap-3" data-id="element-1783" data-mp="36n7pn49tc6yt">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-1784" data-mp="3bin0rogfrorj" /> OKTA
                  Certified Professional
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NRS */}
      <section className="section-light-blue" data-id="element-1785" data-mp="2ix3wjqu93pm2">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1786" data-mp="2nu3zhsvt0c8y">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1787" data-mp="2rvpcde8c8qj5">
            Why Choose New River Systems for Federal ICAM?
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-10" data-id="element-1788" data-mp="33d0mlw3wtme"></div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1789" data-mp="74ykir726863">
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-1790" data-mp="1roafiqhe8oqk">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-1791" data-mp="1nmfg5rj0wlff" />
              <div data-id="element-1792" data-mp="1noji43xd3ad2">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-1793" data-mp="1iv78azxcd84d">
                  Cleared Professional Teams
                </span>
                <span className="text-gray-600 text-sm" data-id="element-1794" data-mp="1dvujwaqtnszo">
                  Identity engineers cleared for secure government environments
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-1795" data-mp="13xeovwoobzkw">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-1796" data-mp="zxmjfsuku9jr" />
              <div data-id="element-1797" data-mp="uy9u91g8gqe0">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-1798" data-mp="uytckj9spgsk">
                  19 Years Federal Experience
                </span>
                <span className="text-gray-600 text-sm" data-id="element-1799" data-mp="2pbj8f6jq502l">
                  Proven track record with DoD, federal agencies, and
                  enterprises
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-1800" data-mp="3d6mcgy5c8oo8">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-1801" data-mp="2zfg6cj7jzssd" />
              <div data-id="element-1802" data-mp="2zdmv0qlzwke1">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-1803" data-mp="34eavs4010flg">
                  Zero Trust Specialists
                </span>
                <span className="text-gray-600 text-sm" data-id="element-1804" data-mp="3s5g9odmeoxt4">
                  Deep expertise implementing Zero Trust Architecture and
                  continuous authentication
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-1805" data-mp="3i8bmqbkqkyb9">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-1806" data-mp="3n37inmt3g5tn" />
              <div data-id="element-1807" data-mp="26p7iqbu90g6e">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-1808" data-mp="26svelhn61eoa">
                  Full Lifecycle Support
                </span>
                <span className="text-gray-600 text-sm" data-id="element-1809" data-mp="245ump8gbxl9k">
                  Strategy through operations – we design, build, and operate
                  your identity infrastructure
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-1810" data-mp="287posf6342oe">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1811" data-mp="288qzgij6un5l">
          <h3 className="text-3xl font-bold mb-4 text-white" data-id="element-1812" data-mp="1kiwpc8q97cp1">
            Secure Your Federal Identity Infrastructure
          </h3>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-1813" data-mp="1geyr008r65x1">
            Let's discuss how New River Systems can help your agency achieve
            Zero Trust and FICAM compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1814" data-mp="11rvthtkfjx9p">
            <Link to="/contact-us" className="btn-primary" data-id="element-1815" data-mp="11q2hdyr1skub">
              Request an ICAM Assessment
            </Link>
            <Link to="/contact-us" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1816" data-mp="1afja1mwm4sko">
              Contact Our ICAM Team
            </Link>
          </div>
        </div>
      </section>
    </div>;
}